import { Link } from "react-router-dom";
import { mediaUrl } from "../lib/api";
import { formatDate } from "../lib/format";
import { CountdownTimer } from "./CountdownTimer";
import { useLeadDialog } from "../context/LeadDialogContext";
import { useLang } from "../context/LanguageContext";
import { MapPin, ArrowUpRight } from "lucide-react";

export function CourseCard({ course, index = 0 }) {
  const lead = useLeadDialog();
  const { t, lang } = useLang();
  return (
    <article
      data-testid={`course-card-${course.slug}`}
      className="group flex flex-col bg-[#E9EFFA] rounded-sm overflow-hidden fade-up"
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      <Link to={`/corsi/${course.slug}`} className="block relative overflow-hidden aspect-[4/3]">
        <img
          src={mediaUrl(course.hero_image)}
          alt={course.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute top-4 left-4 bg-[#F4F8FF]/90 backdrop-blur px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[#1452F0] font-semibold">
          {course.category}
        </span>
        {course.partial_enabled && (
          <span data-testid={`card-partial-badge-${course.slug}`} className="absolute top-4 right-4 bg-[#1452F0] text-white px-3 py-1 text-[11px] uppercase tracking-[0.12em] rounded-full shadow-[0_0_18px_rgba(20,82,240,0.5)]">
            {t("card.online_badge")}
          </span>
        )}
        {course.speaker_photo && (
          <div className="absolute bottom-4 left-4 flex items-center gap-3">
            <img
              src={mediaUrl(course.speaker_photo)}
              alt={course.speaker_name}
              className="h-12 w-12 rounded-full object-cover ring-2 ring-[#F4F8FF]"
            />
            <span className="bg-[#0B1220]/70 backdrop-blur text-[#F4F8FF] text-xs px-3 py-1.5 rounded-full">
              {course.speaker_name}
            </span>
          </div>
        )}
      </Link>

      <div className="flex flex-col flex-1 p-6 md:p-7">
        <Link to={`/corsi/${course.slug}`}>
          <h3 className="font-heading text-2xl md:text-[26px] leading-tight text-[#0B1220] group-hover:text-[#1452F0] transition-colors">
            {course.title}
          </h3>
        </Link>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-[#5C6678]">
          <span>{formatDate(course.date_time, lang)}</span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} /> {course.city}
          </span>
        </div>

        <div className="mt-5 pt-5 border-t border-[#D5DEEE]">
          <span className="overline block mb-2">{t("card.countdown_label")}</span>
          <CountdownTimer date={course.date_time} variant="card" testid={`card-countdown-${course.slug}`} />
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 mt-auto pt-6">
          <Link
            to={`/corsi/${course.slug}`}
            data-testid={`card-discover-${course.slug}`}
            className="flex-1 bg-[#1452F0] text-[#F4F8FF] hover:bg-[#0E3DC0] transition-colors rounded-sm px-5 py-3 text-sm font-medium tracking-wide flex items-center justify-center gap-2"
          >
            {t("card.discover")} <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            data-testid={`card-info-${course.slug}`}
            onClick={() => lead?.openLead(course, "homepage_card")}
            className="flex-1 border border-[#0B1220] text-[#0B1220] hover:bg-[#0B1220] hover:text-[#F4F8FF] transition-colors rounded-sm px-5 py-3 text-sm font-medium tracking-wide"
          >
            {t("card.info")}
          </button>
        </div>
      </div>
    </article>
  );
}
